package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.FormsRepository
import br.com.vvrefeicoes.portal.feature.forms.FormChoiceColumn
import br.com.vvrefeicoes.portal.feature.forms.FormChoiceQuestion
import br.com.vvrefeicoes.portal.feature.forms.FormChoiceTable
import br.com.vvrefeicoes.portal.feature.forms.FormDefinition
import br.com.vvrefeicoes.portal.feature.forms.FormDraft
import br.com.vvrefeicoes.portal.feature.forms.FormFileType
import br.com.vvrefeicoes.portal.feature.forms.FormItem
import br.com.vvrefeicoes.portal.feature.forms.FormItemType
import br.com.vvrefeicoes.portal.feature.forms.FormOption
import br.com.vvrefeicoes.portal.feature.forms.FormQuestion
import br.com.vvrefeicoes.portal.feature.forms.FormQuestionMatrix
import br.com.vvrefeicoes.portal.feature.forms.FormQuestionMatrixColumn
import br.com.vvrefeicoes.portal.feature.forms.FormQuestionMatrixRow
import br.com.vvrefeicoes.portal.feature.forms.FormQuestionTable
import br.com.vvrefeicoes.portal.feature.forms.FormQuestionType
import br.com.vvrefeicoes.portal.feature.forms.FormSection
import br.com.vvrefeicoes.portal.feature.forms.FormSignature
import br.com.vvrefeicoes.portal.feature.forms.FormStatus
import br.com.vvrefeicoes.portal.feature.forms.FormSummary
import br.com.vvrefeicoes.portal.feature.forms.FormTextTable
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

class RemoteFormsRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : FormsRepository {
    private val json =
        Json {
            ignoreUnknownKeys = true
            encodeDefaults = true
        }

    override suspend fun loadForms(): List<FormSummary> = get<List<FormSummaryDto>>("/forms").map { it.toDomain() }

    override suspend fun loadForm(id: String): FormDefinition = get<FormDto>("/forms/$id").toDomain()

    override suspend fun createForm(draft: FormDraft): FormDefinition = post<FormDto, FormDraftDto>("/forms", draft.toDto()).toDomain()

    override suspend fun updateForm(
        id: String,
        draft: FormDraft,
    ): FormDefinition = put<FormDto, FormDraftDto>("/forms/$id", draft.toDto()).toDomain()

    override suspend fun submitForm(id: String): FormDefinition = postEmpty<FormDto>("/forms/$id/submit").toDomain()

    override suspend fun copyForm(id: String): FormDefinition = postEmpty<FormDto>("/forms/$id/copy").toDomain()

    override suspend fun approveForm(id: String): FormDefinition = postEmpty<FormDto>("/forms/$id/approve").toDomain()

    override suspend fun rejectForm(
        id: String,
        reason: String,
    ): FormDefinition = post<FormDto, RejectFormDto>("/forms/$id/reject", RejectFormDto(reason)).toDomain()

    override suspend fun setFormActive(
        id: String,
        active: Boolean,
    ): FormDefinition = patch<FormDto, FormActiveDto>("/forms/$id/active", FormActiveDto(active)).toDomain()

    private suspend inline fun <reified T> get(path: String): T = request { httpClient.get(baseUrl + path) { authorize() } }.body()

    private suspend inline fun <reified T, reified B> post(
        path: String,
        body: B,
    ): T = request { httpClient.post(baseUrl + path) { formJsonBody(body) } }.body()

    private suspend inline fun <reified T> postEmpty(path: String): T = request { httpClient.post(baseUrl + path) { authorize() } }.body()

    private suspend inline fun <reified T, reified B> put(
        path: String,
        body: B,
    ): T = request { httpClient.put(baseUrl + path) { formJsonBody(body) } }.body()

    private suspend inline fun <reified T, reified B> patch(
        path: String,
        body: B,
    ): T = request { httpClient.patch(baseUrl + path) { formJsonBody(body) } }.body()

    private suspend inline fun request(crossinline call: suspend () -> HttpResponse): HttpResponse {
        val response = call()
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        return response
    }

    private fun ensureSuccess(
        status: Int,
        success: Boolean,
        body: String,
    ) {
        if (success) return
        val message =
            runCatching {
                Json { ignoreUnknownKeys = true }
                    .decodeFromString<FormApiErrorDto>(body)
                    .message
            }.getOrNull()
        error(message ?: "HTTP $status")
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private inline fun <reified T> HttpRequestBuilder.formJsonBody(body: T) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(json.encodeToString(body))
    }
}

@Serializable
private data class FormApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class RejectFormDto(
    val reason: String,
)

@Serializable
private data class FormActiveDto(
    val active: Boolean,
)

@Serializable
private data class FormSummaryDto(
    val id: String = "",
    val title: String = "",
    val description: String = "",
    val status: String = "DRAFT",
    val sectors: List<String> = emptyList(),
    val version: Int = 1,
    val createdBy: String = "",
    val updatedAt: String? = null,
    val approvedBy: String? = null,
    val obsolete: Boolean = false,
) {
    fun toDomain() =
        FormSummary(
            id = id,
            title = title,
            description = description,
            status = statusEnum(status),
            sectors = sectors,
            version = version,
            createdBy = createdBy,
            updatedAt = formatIsoDateTime(updatedAt),
            approvedBy = approvedBy,
            obsolete = obsolete,
        )
}

@Serializable
private data class FormDto(
    val id: String = "",
    val familyId: String = "",
    val title: String = "",
    val description: String = "",
    val status: String = "DRAFT",
    val sectors: List<String> = emptyList(),
    val version: Int = 1,
    val createdBy: String = "",
    val updatedAt: String? = null,
    val approvedBy: String? = null,
    val rejectionReason: String? = null,
    val obsolete: Boolean = false,
    val sections: List<FormSectionDto> = emptyList(),
    val signatures: List<FormSignatureDto> = emptyList(),
) {
    fun toDomain() =
        FormDefinition(
            id = id,
            familyId = familyId,
            title = title,
            description = description,
            sectors = sectors,
            status = statusEnum(status),
            version = version,
            createdBy = createdBy,
            updatedAt = formatIsoDateTime(updatedAt),
            approvedBy = approvedBy,
            rejectionReason = rejectionReason,
            obsolete = obsolete,
            sections = sections.map { it.toDomain() },
            signatures = signatures.map { it.toDomain() },
        )
}

private fun statusEnum(value: String): FormStatus = runCatching { FormStatus.valueOf(value) }.getOrDefault(FormStatus.DRAFT)

@Serializable
private data class FormSignatureDto(
    val id: String = "",
    val label: String = "",
) {
    fun toDomain() = FormSignature(id, label)
}

@Serializable
private data class FormSectionDto(
    val id: String = "",
    val title: String = "",
    val items: List<FormItemDto> = emptyList(),
) {
    fun toDomain() =
        FormSection(
            id = id,
            title = title,
            items = items.map { it.toDomain() },
        )
}

@Serializable
private data class FormItemDto(
    val id: String = "",
    val type: String = "TEXT",
    val textHtml: String = "",
    val question: FormQuestionDto? = null,
    val choiceTable: FormChoiceTableDto? = null,
    val questionTable: FormQuestionTableDto? = null,
    val questionMatrix: FormQuestionMatrixDto? = null,
    val textTable: FormTextTableDto? = null,
) {
    fun toDomain() =
        FormItem(
            id = id,
            type = runCatching { FormItemType.valueOf(type) }.getOrDefault(FormItemType.TEXT),
            textHtml = textHtml,
            question = question?.toDomain(),
            choiceTable = choiceTable?.toDomain(),
            questionTable = questionTable?.toDomain(),
            questionMatrix = questionMatrix?.toDomain(),
            textTable = textTable?.toDomain(),
        )
}

@Serializable
private data class FormQuestionDto(
    val id: String = "",
    val title: String = "",
    val type: String = "TEXT",
    val required: Boolean = false,
    val options: List<FormOptionDto> = emptyList(),
    val weight: Int? = null,
    val acceptedFiles: List<String> = emptyList(),
) {
    fun toDomain() =
        FormQuestion(
            id = id,
            title = title,
            type = runCatching { FormQuestionType.valueOf(type) }.getOrDefault(FormQuestionType.TEXT),
            required = required,
            options = options.map { it.toDomain() },
            weight = weight,
            acceptedFiles = acceptedFiles.mapNotNull { runCatching { FormFileType.valueOf(it) }.getOrNull() }.toSet(),
        )
}

@Serializable
private data class FormOptionDto(
    val id: String = "",
    val label: String = "",
    val weight: Int? = null,
) {
    fun toDomain() = FormOption(id, label, weight)
}

@Serializable
private data class FormChoiceTableDto(
    val id: String = "",
    val title: String = "",
    val columns: List<FormChoiceColumnDto> = emptyList(),
    val questions: List<FormChoiceQuestionDto> = emptyList(),
) {
    fun toDomain() =
        FormChoiceTable(
            id = id,
            title = title,
            columns = columns.map { it.toDomain() },
            questions = questions.map { it.toDomain() },
        )
}

@Serializable
private data class FormChoiceColumnDto(
    val id: String = "",
    val label: String = "",
    val weight: Double? = null,
) {
    fun toDomain() = FormChoiceColumn(id, label, weight)
}

@Serializable
private data class FormChoiceQuestionDto(
    val id: String = "",
    val title: String = "",
    val weight: Int? = null,
) {
    fun toDomain() = FormChoiceQuestion(id, title, weight)
}

@Serializable
private data class FormQuestionTableDto(
    val id: String = "",
    val title: String = "",
    val rowCount: Int = 2,
    val columnCount: Int = 1,
    val headers: List<String> = listOf(""),
) {
    fun toDomain() = FormQuestionTable(id, title, rowCount, columnCount, headers)
}

@Serializable
private data class FormQuestionMatrixDto(
    val id: String = "",
    val title: String = "",
    val columns: List<FormQuestionMatrixColumnDto> = emptyList(),
    val rows: List<FormQuestionMatrixRowDto> = emptyList(),
) {
    fun toDomain() =
        FormQuestionMatrix(
            id = id,
            title = title,
            columns = columns.map { FormQuestionMatrixColumn(it.id, it.label) },
            rows = rows.map { FormQuestionMatrixRow(it.id, it.label) },
        )
}

@Serializable
private data class FormQuestionMatrixColumnDto(
    val id: String = "",
    val label: String = "",
)

@Serializable
private data class FormQuestionMatrixRowDto(
    val id: String = "",
    val label: String = "",
)

@Serializable
private data class FormTextTableDto(
    val id: String = "",
    val title: String = "",
    val rowCount: Int = 1,
    val columnCount: Int = 1,
    val cells: List<String> = listOf(""),
) {
    fun toDomain() = FormTextTable(id, title, rowCount, columnCount, cells)
}

@Serializable
private data class FormDraftDto(
    val title: String,
    val description: String,
    val sectors: List<String>,
    val sections: List<FormSectionDto>,
    val signatures: List<FormSignatureDto>,
)

private fun FormDraft.toDto() =
    FormDraftDto(
        title = title,
        description = description,
        sectors = sectors,
        sections = sections.map { it.toDto() },
        signatures = signatures.map { FormSignatureDto(it.id, it.label) },
    )

private fun FormSection.toDto() =
    FormSectionDto(
        id = id,
        title = title,
        items = items.map { it.toDto() },
    )

private fun FormItem.toDto() =
    FormItemDto(
        id = id,
        type = type.name,
        textHtml = textHtml,
        question = question?.toDto(),
        choiceTable = choiceTable?.toDto(),
        questionTable = questionTable?.toDto(),
        questionMatrix = questionMatrix?.toDto(),
        textTable = textTable?.toDto(),
    )

private fun FormQuestion.toDto() =
    FormQuestionDto(
        id = id,
        title = title,
        type = type.name,
        required = required,
        options = options.map { FormOptionDto(it.id, it.label, it.weight) },
        weight = weight,
        acceptedFiles = acceptedFiles.map { it.name },
    )

private fun FormChoiceTable.toDto() =
    FormChoiceTableDto(
        id = id,
        title = title,
        columns = columns.map { FormChoiceColumnDto(it.id, it.label, it.weight) },
        questions = questions.map { FormChoiceQuestionDto(it.id, it.title, it.weight) },
    )

private fun FormQuestionTable.toDto() =
    FormQuestionTableDto(
        id = id,
        title = title,
        rowCount = rowCount,
        columnCount = columnCount,
        headers = headers,
    )

private fun FormQuestionMatrix.toDto() =
    FormQuestionMatrixDto(
        id = id,
        title = title,
        columns = columns.map { FormQuestionMatrixColumnDto(it.id, it.label) },
        rows = rows.map { FormQuestionMatrixRowDto(it.id, it.label) },
    )

private fun FormTextTable.toDto() =
    FormTextTableDto(
        id = id,
        title = title,
        rowCount = rowCount,
        columnCount = columnCount,
        cells = cells,
    )
